/**
 * Recursively checks if a target object contains all the keys and nested keys
 * from a default/template object.
 *
 * @param {object} target The object to check (e.g., terafy.project).
 * @param {object} defaults The template object with the required keys (e.g., defaultProjectState).
 * @returns {boolean} Returns `true` if all keys from `defaults` exist in `target`, otherwise `false`.
 */
export const checkAllKeysExist = (target, defaults) => {
	// Ensure the target is an object. If not, it can't have the required keys.
	if (typeof target !== "object" || target === null) {
		return false;
	}

	// Use Object.keys for a clean loop over the blueprint object.
	return Object.keys(defaults).every(key => {
		// Check if the key from the default object exists in the target object.
		if (!Object.prototype.hasOwnProperty.call(target, key)) {
			return false; // Key is missing, so we fail immediately.
		}

		const defaultValue = defaults[key];
		const targetValue = target[key];

		// If the default's value is a plain object, we must recurse.
		// This check ensures we only dive into objects, not arrays or null.
		if (
			typeof defaultValue === "object" &&
			defaultValue !== null &&
			!Array.isArray(defaultValue)
		) {
			// The key exists, but we need to check its children.
			// The target's value must also be an object to continue the check.
			if (typeof targetValue !== "object" || targetValue === null) {
				return false; // Structural mismatch: expected an object, found something else.
			}
			// Recurse into the nested object.
			return checkAllKeysExist(targetValue, defaultValue);
		}

		// If it's not a nested object, just the key's presence is enough.
		return true;
	});
};
